import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, deleteField, collection, query, where, orderBy, addDoc, getDocs, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { MapPin, Edit, Trash2, ArrowLeft, Clock, User as UserIcon, Phone, MessageSquare, Upload, Image as ImageIcon, ZoomIn, Dog, Star, Calendar as CalendarIcon, CreditCard, Edit3, Plus, DollarSign, Car } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import GalleryImageModal from '../components/GalleryImageModal';
import Portal from '../components/Portal';
import ImageCropModal from '../components/ImageCropModal';
import { generateMonthlyAppointments, updateRecurringAppointments } from '../utils/recurringAppointments';
import { useAuth } from '../contexts/AuthContext';
import { useTransactionTypes } from '../contexts/TransactionTypesContext';
import { updateTransactionsAggregationOnCreate, updateTransactionsAggregationOnUpdate, updateTransactionsAggregationOnDelete } from '../utils/transactionsAggregations';
import { updatePetsAggregationOnUpdate, updatePetsAggregationOnDelete } from '../utils/petsAggregations';
import { useAppointmentsAggregate } from '../utils/useAppointmentsAggregate';
import { updateAppointmentsAggregationOnCreate, updateAppointmentsAggregationOnUpdate, updateAppointmentsAggregationOnDelete } from '../utils/appointmentsAggregations';
import { useTransactionsAggregate } from '../utils/useTransactionsAggregate';

const PetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [pet, setPet] = useState(null);
  const [formData, setFormData] = useState({ 
    nome: '', 
    raca: '', 
    mesNascimento: '', 
    anoNascimento: '', 
    porte: '', 
    pelagem: '', 
    tutor: '', 
    telefone: '', 
    endereco: '',
    observacoes: '',
    fonteContato: '',
    temAlergia: false,
    detalhesAlergia: '',
    temDorArticular: false,
    detalhesDorArticular: '',
    usaMedicamento: false,
    detalhesMedicamento: '',
    acompanhamentoVeterinario: false,
    detalhesAcompanhamento: '',
    autorizaImagem: false,
    planoMensal: false,
    diasAgendados: [],
    intervaloRecorrente: 'weekly', // 'weekly' ou 'biweekly' (a cada 2 semanas)
    tipoServicoPlanoMensal: '',
    precoPlanoMensal: 0,
    dataInicioPlanoMensal: '', // Data de início do plano mensal
    horaInicioPlanoMensal: '' // Horário de início do plano mensal
  });
  // Usar agregado de agendamentos
  const { appointments: allAppointments, loading: appointmentsLoading, refresh: refreshAppointments } = useAppointmentsAggregate();
  // Usar agregado de transações
  const { transactions: allTransactions, refresh: refreshTransactions } = useTransactionsAggregate();
  const [appointments, setAppointments] = useState([]);
  const notesTextareaRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [galleryImage, setGalleryImage] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [editingMonthlyPlan, setEditingMonthlyPlan] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planValidationErrors, setPlanValidationErrors] = useState({});
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    tipoServico: '',
    dataAgendamento: '',
    horaAgendamento: '',
    servicePrice: '',
    hasTransport: false,
    transportPrice: '',
    paymentMethod: '',
    observacoes: ''
  });
  const [submittingAppointment, setSubmittingAppointment] = useState(false);
  const daysOfWeek = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'];

  // Função helper para converter valor do porte em label
  const getSizeLabel = (size) => {
    const labels = {
      'mini': 'Mini - até 4 kg',
      'pequeno': 'Pequeno - entre 4 kg e 10 kg',
      'medio': 'Médio - entre 10 kg e 25 kg',
      'grande': 'Grande - entre 25 kg e 45 kg',
      'gigante': 'Gigante - acima de 45 kg'
    };
    return labels[size] || size;
  };

  // Função helper para converter valor da pelagem em label
  const getCoatLabel = (coat) => {
    const labels = {
      'curto': 'Curto',
      'medio': 'Médio',
      'longo': 'Longo'
    };
    return labels[coat] || coat;
  };

  // Função helper para formatar telefone
  const formatPhone = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Limita a 11 dígitos (máximo para celular brasileiro)
    const limitedNumbers = numbers.slice(0, 11);
    
    // Formata conforme o tamanho
    if (limitedNumbers.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return limitedNumbers.replace(/(\d{2})(\d{4})(\d{0,4})/, (match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return limitedNumbers;
      });
    } else {
      // Celular: (XX) XXXXX-XXXX
      return limitedNumbers.replace(/(\d{2})(\d{5})(\d{0,4})/, (match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return limitedNumbers;
      });
    }
  };
  const { confirmation, showConfirmation, hideConfirmation, confirmAction, cancelAction } = useConfirmation();
  const { user } = useAuth();
  const { transactionTypes } = useTransactionTypes();

  useEffect(() => {
    const fetchPet = async () => {
      try {
        const snap = await getDoc(doc(db, 'pets', id));
        if (!snap.exists()) {
          setError('Pet não encontrado');
        } else {
          const data = { id: snap.id, ...snap.data() };
          setPet(data);
          // galeria: null=sem fotos, true=tem fotos, string=URL da imagem com estrela
          const starredUrl = typeof data.galeria === 'string' ? data.galeria : null;
          setGalleryImage(starredUrl);
          setFormData({
            nome: data.nome || '',
            raca: data.raca || '',
            mesNascimento: data.mesNascimento || '',
            anoNascimento: data.anoNascimento || '',
            porte: data.porte || '',
            pelagem: data.pelagem || '',
            tutor: data.tutor || '',
            telefone: data.telefone || '',
            endereco: data.endereco || '',
            observacoes: data.observacoes || '',
            fonteContato: data.fonteContato || '',
            temAlergia: data.temAlergia || false,
            detalhesAlergia: data.detalhesAlergia || '',
            temDorArticular: data.temDorArticular || false,
            detalhesDorArticular: data.detalhesDorArticular || '',
            usaMedicamento: data.usaMedicamento || false,
            detalhesMedicamento: data.detalhesMedicamento || '',
            acompanhamentoVeterinario: data.acompanhamentoVeterinario || false,
            detalhesAcompanhamento: data.detalhesAcompanhamento || '',
            autorizaImagem: data.autorizaImagem || false,
            planoMensal: data.planoMensal || false,
            diasAgendados: data.diasAgendados || [],
            intervaloRecorrente: data.intervaloRecorrente || 'weekly',
            tipoServicoPlanoMensal: data.tipoServicoPlanoMensal || data.planoMensalServiceType || '',
            precoPlanoMensal: data.precoPlanoMensal || data.planoMensalPrice || 0,
            dataInicioPlanoMensal: data.dataInicioPlanoMensal || data.planoMensalStartDate || '',
            horaInicioPlanoMensal: data.horaInicioPlanoMensal || data.planoMensalStartTime || ''
          });
        }
      } catch (e) {
        setError('Erro ao carregar pet');
      } finally {
        setLoading(false);
      }
    };
    fetchPet();
  }, [id]);

  useEffect(() => {
    if (!id || !pet) return;
    loadImages();
  }, [id, pet]);


  const getServiceTypeLabel = (type) => {
    return type || 'Serviço não informado';
  };

  // Função para extrair apenas o primeiro nome com apenas a primeira letra maiúscula
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    const firstName = fullName.trim().split(' ')[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  };

  // Função para formatar nome com apenas as primeiras letras maiúsculas
  const formatName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getServiceColor = (type) => {
    return 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300';
  };

  // Função para extrair o número de anos da string de idade
  const extractYearsFromAge = (ageString) => {
    if (!ageString) return null;
    // Se for apenas um número, retornar diretamente
    const numericValue = parseInt(ageString, 10);
    if (!isNaN(numericValue)) {
      return numericValue;
    }
    // Caso contrário, tentar extrair de padrões como "2 anos", "2 ano", "2a", etc.
    const match = ageString.match(/(\d+)\s*(ano|anos|a)/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    return null;
  };

  // Função para calcular a idade atual baseada na data de nascimento ou na data em que foi definida
  const calculateCurrentAge = (pet) => {
    if (!pet) return 'Não informada';
    
    // Prioridade 1: Calcular a partir de birthMonth e birthYear se disponível
    if (pet.mesNascimento && pet.anoNascimento) {
      const monthNames = {
        'Janeiro': 0, 'Fevereiro': 1, 'Março': 2, 'Abril': 3,
        'Maio': 4, 'Junho': 5, 'Julho': 6, 'Agosto': 7,
        'Setembro': 8, 'Outubro': 9, 'Novembro': 10, 'Dezembro': 11
      };
      
      const birthMonthIndex = monthNames[pet.mesNascimento];
      if (birthMonthIndex !== undefined) {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const birthYearNum = parseInt(pet.anoNascimento, 10);
        
        if (!isNaN(birthYearNum)) {
          // Calcular diferença em meses
          let totalMonths = (currentYear - birthYearNum) * 12 + (currentMonth - birthMonthIndex);
          
          if (totalMonths < 0) {
            return 'Não informada';
          }
          
          // Se tiver menos de 12 meses, mostrar em meses
          if (totalMonths < 12) {
            return totalMonths === 1 ? '1 mês' : `${totalMonths} meses`;
          }
          
          // Se tiver 12 meses ou mais, calcular anos e meses restantes
          const years = Math.floor(totalMonths / 12);
          const months = totalMonths % 12;
          
          if (months === 0) {
            return years === 1 ? '1 ano' : `${years} anos`;
          } else {
            const yearsText = years === 1 ? '1 ano' : `${years} anos`;
            const monthsText = months === 1 ? '1 mês' : `${months} meses`;
            return `${yearsText} e ${monthsText}`;
          }
        }
      }
    }
    
    // Prioridade 2: Se houver ageBaseValue e ageBaseDate, calcular a idade atual
    if (pet.ageBaseValue !== undefined && pet.ageBaseValue !== null && pet.ageBaseDate) {
      const baseDate = pet.ageBaseDate?.toDate ? pet.ageBaseDate.toDate() : new Date(pet.ageBaseDate);
      const now = new Date();
      const yearsDiff = now.getFullYear() - baseDate.getFullYear();
      const monthsDiff = now.getMonth() - baseDate.getMonth();
      const daysDiff = now.getDate() - baseDate.getDate();
      
      // Calcular anos completos decorridos
      let yearsPassed = yearsDiff;
      if (monthsDiff < 0 || (monthsDiff === 0 && daysDiff < 0)) {
        yearsPassed--;
      }
      
      const currentAge = pet.ageBaseValue + yearsPassed;
      
      // Retornar apenas o número (compatibilidade com sistema antigo)
      return currentAge >= 0 ? currentAge.toString() : 'Não informada';
    }
    
    // Prioridade 3: Se houver idade como número, retornar diretamente
    if (pet.idade) {
      const numericAge = parseInt(pet.idade, 10);
      if (!isNaN(numericAge)) {
        // Se a idade for 0, pode ser que tenha meses
        if (numericAge === 0) {
          return 'Menos de 1 ano';
        }
        return numericAge === 1 ? '1 ano' : `${numericAge} anos`;
      }
      return pet.idade;
    }
    
    return 'Não informada';
  };

  // Funções para gerenciar imagens — path: pets/{id}_{nomeDoPet}/
  const loadImages = async () => {
    if (!id || !pet) return;
    setLoadingImages(true);
    try {
      const petNameSlug = sanitizePetNameForPath(pet?.nome);
      const pathWithName = `pets/${id}_${petNameSlug}`;
      const pathLegacy = `pets/${id}`;
      const allItems = [];

      try {
        const refWithName = ref(storage, pathWithName);
        const listWithName = await listAll(refWithName);
        allItems.push(...listWithName.items);
      } catch (_) {
        // Pasta nova pode não existir ainda
      }
      if (pathWithName !== pathLegacy) {
        try {
          const refLegacy = ref(storage, pathLegacy);
          const listLegacy = await listAll(refLegacy);
          allItems.push(...listLegacy.items);
        } catch (_) {
          // Pasta antiga pode não existir
        }
      }

      const seen = new Set();
      const imageUrls = await Promise.all(
        allItems
          .filter((imageRef) => {
            if (seen.has(imageRef.fullPath)) return false;
            seen.add(imageRef.fullPath);
            return true;
          })
          .map(async (imageRef) => {
            const url = await getDownloadURL(imageRef);
            return {
              nome: imageRef.name,
              url: url,
              fullPath: imageRef.fullPath
            };
          })
      );

      setImages(imageUrls);
    } catch (error) {
      // Erro ao carregar imagens
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validar limite de 4 fotos
    const totalImages = images.length + files.length;
    if (totalImages > 4) {
      await showConfirmation({
        title: 'Limite de Fotos Excedido',
        messidade: `Você só pode adicionar ${4 - images.length} foto(s) a mais. O limite máximo é de 4 fotos por pet.`,
        confirmText: 'OK',
        showCancel: false,
        type: 'warning'
      });
      event.target.value = ''; // Limpar input
      return;
    }

    // Validar arquivos
    const invalidFiles = [];
    const oversizedFiles = [];
    const validFiles = [];

    for (const file of files) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        invalidFiles.push(file.nome);
        continue;
      }

      // Validar tamanho (máximo 15MB)
      if (file.porte > 15 * 1024 * 1024) {
        oversizedFiles.push({
          nome: file.nome,
          porte: (file.porte / (1024 * 1024)).toFixed(2)
        });
        continue;
      }

      validFiles.push(file);
    }

    // Mostrar avisos de arquivos rejeitados
    if (invalidFiles.length > 0 || oversizedFiles.length > 0) {
      let message = '';
      
      if (invalidFiles.length > 0) {
        message += `Arquivos não são imagens e foram ignorados:\n• ${invalidFiles.join('\n• ')}\n\n`;
      }
      
      if (oversizedFiles.length > 0) {
        message += `Arquivos excedem o limite de 15MB e foram ignorados:\n${oversizedFiles.map(f => `• ${f.nome} (${f.porte}MB)`).join('\n')}`;
      }

      await showConfirmation({
        title: 'Arquivos Não Foram Aceitos',
        messidade: message.trim(),
        confirmText: 'OK',
        showCancel: false,
        type: 'warning'
      });
    }

    if (validFiles.length === 0) {
      event.target.value = '';
      return;
    }

    // Armazenar arquivos pendentes e abrir modal de crop para o primeiro
    setPendingFiles(validFiles);
    setImageToCrop(validFiles[0]);
    setShowCropModal(true);
    
    // Limpar input
    event.target.value = '';
  };

  // Sanitiza o nome do pet para uso no path do Storage (sem espaços/caracteres especiais)
  const sanitizePetNameForPath = (name) => {
    if (!name || typeof name !== 'string') return 'pet';
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .slice(0, 50) || 'pet';
  };

  const handleCropComplete = async (croppedFile) => {
    // Processar upload do arquivo cortado
    setUploading(true);
    setUploadProgress(0);

    try {
      const timestamp = Date.now();
      const petNameSlug = sanitizePetNameForPath(pet?.nome);
      const ext = croppedFile.name?.split('.').pop() || 'jpg';
      // Path: pets/{id}_{nomeDoPet}/{timestamp}.ext — ID e nome do pet juntos na pasta
      const fileName = `${timestamp}.${ext}`;
      const imageRef = ref(storage, `pets/${id}_${petNameSlug}/${fileName}`);
      
      await uploadBytes(imageRef, croppedFile);
      const downloadURL = await getDownloadURL(imageRef);
      
      const isFirstImage = images.length === 0;
      setImages(prev => [...prev, {
        nome: fileName,
        url: downloadURL,
        fullPath: imageRef.fullPath
      }]);
      
      if (isFirstImage) {
        const updatePayload = { galeria: true, atualizadoEm: Timestamp.now() };
        await updateDoc(doc(db, 'pets', id), updatePayload);
        const updatedPetData = { ...pet, ...updatePayload };
        setPet(updatedPetData);
        try {
          await updatePetsAggregationOnUpdate(updatedPetData);
        } catch (aggError) {
          console.error('Erro ao atualizar agregado de pets:', aggError);
        }
      }
      
      setUploadProgress(100);

      // Se há mais arquivos pendentes, processar o próximo
      if (pendingFiles.length > 1) {
        const remainingFiles = pendingFiles.slice(1);
        setPendingFiles(remainingFiles);
        setImageToCrop(remainingFiles[0]);
        // Manter modal aberto para próximo arquivo
      } else {
        // Fechar modal e limpar estados
        setShowCropModal(false);
        setImageToCrop(null);
        setPendingFiles([]);
        setUploading(false);
        setUploadProgress(0);
      }
    } catch (error) {
      await showConfirmation({
        title: 'Erro no Upload',
        messidade: 'Ocorreu um erro ao fazer upload da imagem. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      setShowCropModal(false);
      setImageToCrop(null);
      setPendingFiles([]);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCropCancel = () => {
    // Se há mais arquivos, processar o próximo
    if (pendingFiles.length > 1) {
      const remainingFiles = pendingFiles.slice(1);
      setPendingFiles(remainingFiles);
      setImageToCrop(remainingFiles[0]);
    } else {
      // Fechar modal e limpar estados
      setShowCropModal(false);
      setImageToCrop(null);
      setPendingFiles([]);
    }
  };

  const handleImageDelete = async (imageToDelete) => {
    try {
      const imageRef = ref(storage, imageToDelete.fullPath);
      await deleteObject(imageRef);
      const remainingCount = images.length - 1;
      const wasStarred = galleryImage === imageToDelete.url;
      const galeriaValue = remainingCount === 0 ? null : (wasStarred ? true : pet.galeria);
      if (wasStarred || remainingCount === 0) {
        const galeriaToWrite = galeriaValue === null ? deleteField() : galeriaValue;
        await updateDoc(doc(db, 'pets', id), {
          galeria: galeriaToWrite,
          atualizadoEm: Timestamp.now()
        });
        const updatedPetData = { ...pet, galeria: galeriaValue, atualizadoEm: Timestamp.now() };
        try {
          await updatePetsAggregationOnUpdate(updatedPetData);
        } catch (aggError) {}
        setPet(updatedPetData);
        if (wasStarred) setGalleryImage(null);
      }
      setImages(prev => prev.filter(img => img.fullPath !== imageToDelete.fullPath));
    } catch (error) {
      // Erro ao deletar imagem
    }
  };

  const handleImageDeleteWithConfirmation = async (imageToDelete) => {
    const confirmed = await showConfirmation({
      title: 'Excluir Foto',
      messidade: 'Tem certeza que deseja excluir esta foto? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    await handleImageDelete(imageToDelete);
    closeImageModal();
  };

  const handleSetGalleryImage = async (image) => {
    try {
      const isUnstarring = galleryImage === image.url;
      // Ao desmarcar: sempre há pelo menos 1 imagem, então galeria = true. null só ao deletar todas.
      const newGaleria = isUnstarring ? true : image.url;
      const petRef = doc(db, 'pets', id);
      if (newGaleria === null) {
        await updateDoc(petRef, { galeria: deleteField(), atualizadoEm: Timestamp.now() });
      } else {
        await updateDoc(petRef, { galeria: newGaleria, atualizadoEm: Timestamp.now() });
      }
      const updatedPetData = {
        ...pet,
        galeria: newGaleria === true ? true : newGaleria,
        atualizadoEm: Timestamp.now()
      };
      setPet(updatedPetData);
      setGalleryImage(typeof newGaleria === 'string' ? newGaleria : null);
      try {
        await updatePetsAggregationOnUpdate(updatedPetData);
      } catch (aggError) {
        console.error('Erro ao atualizar agregado de pets:', aggError);
      }
    } catch (error) {}
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    
    const currentIndex = images.findIndex(img => img.fullPath === selectedImage.fullPath);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    } else {
      newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedImage(images[newIndex]);
  };

  // Ajustar altura do textarea de observações quando entrar em modo de edição
  useEffect(() => {
    if (isEditing && notesTextareaRef.current) {
      // Aguardar um frame para garantir que o DOM está atualizado
      setTimeout(() => {
        if (notesTextareaRef.current) {
          notesTextareaRef.current.style.height = 'auto';
          notesTextareaRef.current.style.height = `${Math.max(2.625 * 16, notesTextareaRef.current.scrollHeight)}px`;
        }
      }, 0);
    }
  }, [isEditing, formData.observacoes]);



  // Filtrar agendamentos do pet do agregado
  useEffect(() => {
    if (appointmentsLoading) {
      return;
    }

    // Filtrar agendamentos do pet específico do agregado
    const petAppointments = allAppointments
      .filter(apt => apt.idPet === id)
      .sort((a, b) => {
        const dateA = a.dataAgendamento instanceof Date ? a.dataAgendamento : new Date(a.dataAgendamento);
        const dateB = b.dataAgendamento instanceof Date ? b.dataAgendamento : new Date(b.dataAgendamento);
        return dateB.getTime() - dateA.getTime(); // Mais recente primeiro
      });

    setAppointments(petAppointments);
  }, [allAppointments, appointmentsLoading, id]);

  // Agendamentos já vêm ordenados da query (mais recentes primeiro)
  const sortedAppointments = appointments;

  // Função para editar agendamento
  const handleEditAppointment = (appointment) => {
    let appointmentDate;
    if (appointment.dataAgendamento?.toDate) {
      appointmentDate = appointment.dataAgendamento.toDate();
    } else if (appointment.dataAgendamento instanceof Date) {
      appointmentDate = appointment.dataAgendamento;
    } else {
      appointmentDate = new Date(appointment.dataAgendamento);
    }
    
    const dateStr = appointmentDate.toISOString().split('T')[0];
    const timeStr = appointmentDate.toTimeString().slice(0, 5);
    
    setEditingAppointment(appointment);
    setAppointmentForm({
      tipoServico: appointment.tipoServico || '',
      dataAgendamento: dateStr,
      horaAgendamento: timeStr,
      servicePrice: appointment.servicePrice ? appointment.servicePrice.toString() : '',
      hasTransport: appointment.hasTransport || (appointment.transportPrice > 0),
      transportPrice: appointment.transportPrice ? appointment.transportPrice.toString() : '',
      paymentMethod: appointment.paymentMethod || '',
      observacoes: appointment.observacoes || ''
    });
    setShowAppointmentModal(true);
  };

  // Função para atualizar agendamento
  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      await showConfirmation({
        title: 'Erro de autenticação',
        messidade: 'Você precisa estar logado para salvar agendamentos.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      return;
    }
    
    if (!appointmentForm.tipoServico || !appointmentForm.dataAgendamento || !appointmentForm.horaAgendamento) {
      await showConfirmation({
        title: 'Campos obrigatórios',
        messidade: 'Por favor, preencha todos os campos obrigatórios: Tipo de Serviço, Data e Hora.',
        confirmText: 'OK',
        showCancel: false,
        type: 'warning'
      });
      return;
    }

    setSubmittingAppointment(true);

    try {
      if (editingAppointment) {
        // Atualizar agendamento existente
        const appointmentRef = doc(db, 'agendamentos', editingAppointment.id);
        const scheduledDateTime = new Date(`${appointmentForm.dataAgendamento}T${appointmentForm.horaAgendamento}`);
        await updateDoc(appointmentRef, {
          idPet: id,
          nomePet: pet?.nome || '',
          ownernome: pet?.tutor || '',
          telefone: pet?.telefone || '',
          tipoServico: appointmentForm.tipoServico,
          dataAgendamento: Timestamp.fromDate(scheduledDateTime),
          servicePrice: appointmentForm.servicePrice ? parseFloat(appointmentForm.servicePrice) : 0,
          hasTransport: appointmentForm.hasTransport || false,
          transportPrice: appointmentForm.hasTransport && appointmentForm.transportPrice ? parseFloat(appointmentForm.transportPrice) : 0,
          paymentMethod: appointmentForm.paymentMethod || '',
          observacoes: appointmentForm.observacoes || '',
          atualizadoEm: Timestamp.now(),
          atualizadoPor: user ? {
            uid: user.uid,
            email: user.email || '',
            nomeExibicao: user.nomeExibicao || (user.email ? user.email.split('@')[0] : 'Usuário')
          } : null
        });

        // Otimização: usar dados que já temos ao invés de ler do Firestore
        const updatedAppointmentData = {
          id: editingAppointment.id,
          idPet: id,
          nomePet: pet?.nome || '',
          ownernome: pet?.tutor || '',
          telefone: pet?.telefone || '',
          tipoServico: appointmentForm.tipoServico,
          dataAgendamento: Timestamp.fromDate(scheduledDateTime),
          servicePrice: appointmentForm.servicePrice ? parseFloat(appointmentForm.servicePrice) : 0,
          hasTransport: appointmentForm.hasTransport || false,
          transportPrice: appointmentForm.hasTransport && appointmentForm.transportPrice ? parseFloat(appointmentForm.transportPrice) : 0,
          paymentMethod: appointmentForm.paymentMethod || '',
          observacoes: appointmentForm.observacoes || '',
          atualizadoEm: Timestamp.now(),
          atualizadoPor: user ? {
            uid: user.uid,
            email: user.email || '',
            nomeExibicao: user.nomeExibicao || (user.email ? user.email.split('@')[0] : 'Usuário')
          } : null,
          // Preservar campos do agendamento original que não estão no form
          criadoEm: editingAppointment.criadoEm || Timestamp.now(),
          criadoPor: editingAppointment.criadoPor || null
        };
        
        // Preparar oldAppointmentData para comparação (usar editingAppointment original)
        const oldAppointmentData = {
          id: editingAppointment.id,
          dataAgendamento: editingAppointment.dataAgendamento
        };
        
        // Atualizar agregado de agendamentos
        try {
          await updateAppointmentsAggregationOnUpdate(oldAppointmentData, updatedAppointmentData);
        } catch (err) {
          // Erro ao atualizar agregado de agendamentos
        }

        // Buscar transação vinculada ao agendamento do agregado
        const linkedTransaction = allTransactions.find(t => (t.idAgendamento || t.appointmentId) === editingAppointment.id);
        const hasTransaction = !!linkedTransaction;

        if (appointmentForm.servicePrice && parseFloat(appointmentForm.servicePrice) > 0) {
          const transactionDate = new Date(`${appointmentForm.dataAgendamento}T${appointmentForm.horaAgendamento}`);
          // Se houver transação vinculada, atualizar
          if (hasTransaction && linkedTransaction) {
            const oldTransactionData = linkedTransaction;
            const newTransactionData = {
              tipo: 'receita',
              idPet: id,
              valor: parseFloat(appointmentForm.servicePrice),
              idTipoTransacao: '',
              nomeTipoTransacao: 'Agendamentos',
              descricao: appointmentForm.observacoes || '',
              data: Timestamp.fromDate(transactionDate),
              idAgendamento: editingAppointment.id
            };
            
            await updateDoc(doc(db, 'transacoes', linkedTransaction.id), {
              idPet: id,
              valor: parseFloat(appointmentForm.servicePrice),
              idTipoTransacao: '',
              nomeTipoTransacao: 'Agendamentos',
              descricao: appointmentForm.observacoes || '',
              data: Timestamp.fromDate(transactionDate),
              atualizadoEm: Timestamp.now()
            });
            
            // Otimização: usar dados que já temos ao invés de ler do Firestore
            const nomePet = pet?.nome || null;
            
            const updatedTransactionData = {
              id: linkedTransaction.id,
              tipo: 'receita',
              idPet: id,
              valor: parseFloat(appointmentForm.servicePrice),
              idTipoTransacao: '',
              nomeTipoTransacao: 'Agendamentos',
              descricao: appointmentForm.observacoes || '',
              data: Timestamp.fromDate(transactionDate),
              idAgendamento: editingAppointment.id,
              atualizadoEm: Timestamp.now(),
              nomePet: nomePet,
              // Preservar campos da transação original
              criadoEm: linkedTransaction.criadoEm || Timestamp.now(),
              criadoPor: linkedTransaction.criadoPor || null
            };
            
            // Atualizar agregado de transações
            try {
              await updateTransactionsAggregationOnUpdate(linkedTransaction, updatedTransactionData);
            } catch (err) {
              // Erro ao atualizar agregado de transações
            }
            
            // Recarregar agregado de transações após atualização
            if (refreshTransactions) {
              await refreshTransactions();
            }
          } else {
            // Se não houver transação vinculada, criar uma nova
            const transactionData = {
              tipo: 'receita',
              idPet: id,
              valor: parseFloat(appointmentForm.servicePrice),
              idTipoTransacao: '',
              nomeTipoTransacao: 'Agendamentos',
              descricao: appointmentForm.observacoes || '',
              data: Timestamp.fromDate(transactionDate),
              idAgendamento: editingAppointment.id,
              criadoEm: Timestamp.now(),
              criadoPor: user ? {
                uid: user.uid,
                nomeExibicao: user.nomeExibicao || 'Usuário'
              } : null
            };

            const transactionDocRef = await addDoc(collection(db, 'transacoes'), transactionData);
            
            // Otimização: usar dados que já temos ao invés de ler do Firestore
            const nomePet = pet?.nome || null;
            
            const newTransactionData = {
              id: transactionDocRef.id,
              tipo: 'receita',
              idPet: id,
              valor: parseFloat(appointmentForm.servicePrice),
              idTipoTransacao: '',
              nomeTipoTransacao: 'Agendamentos',
              descricao: appointmentForm.observacoes || '',
              data: Timestamp.fromDate(transactionDate),
              idAgendamento: editingAppointment.id,
              criadoEm: Timestamp.now(),
              criadoPor: user ? {
                uid: user.uid,
                nomeExibicao: user.nomeExibicao || 'Usuário'
              } : null,
              nomePet: nomePet
            };
            
            // Atualizar agregado de transações
            try {
              await updateTransactionsAggregationOnCreate(newTransactionData);
            } catch (err) {
              // Erro ao atualizar agregado de transações
            }
            
            // Recarregar agregado de transações após criação
            if (refreshTransactions) {
              await refreshTransactions();
            }
          }
        } else {
          // Se o preço foi removido, excluir a transação vinculada se existir
          if (hasTransaction && linkedTransaction) {
            const transactionData = linkedTransaction;
            await deleteDoc(doc(db, 'transacoes', linkedTransaction.id));
            
            // Atualizar agregado de transações (aguardar conclusão)
            try {
              await updateTransactionsAggregationOnDelete(linkedTransaction.id, linkedTransaction.data || linkedTransaction.date);
            } catch (err) {
                                        // Erro ao atualizar agregado de transações
            }
            
            // Recarregar agregado de transações após exclusão
            if (refreshTransactions) {
              await refreshTransactions();
            }
          }
        }

        // Atualizar o estado local com o agendamento editado (feedback imediato)
        setAppointments(prev => prev.map(apt => {
          if (apt.id === editingAppointment.id) {
            return {
              ...apt,
              tipoServico: appointmentForm.tipoServico,
              dataAgendamento: Timestamp.fromDate(scheduledDateTime),
              servicePrice: appointmentForm.servicePrice ? parseFloat(appointmentForm.servicePrice) : 0,
              hasTransport: appointmentForm.hasTransport || false,
              transportPrice: appointmentForm.hasTransport && appointmentForm.transportPrice ? parseFloat(appointmentForm.transportPrice) : 0,
              paymentMethod: appointmentForm.paymentMethod || '',
              observacoes: appointmentForm.observacoes || ''
            };
          }
          return apt;
        }));
        
        // Recarregar agregado de agendamentos para garantir sincronização
        if (refreshAppointments) {
          await refreshAppointments();
        }
      }
      
      // Limpar formulário
      setAppointmentForm({
        tipoServico: '',
        dataAgendamento: '',
        horaAgendamento: '',
        servicePrice: '',
        hasTransport: false,
        transportPrice: '',
        paymentMethod: '',
        observacoes: ''
      });
      setEditingAppointment(null);
      setShowAppointmentModal(false);
      
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        messidade: `Erro ao salvar agendamento: ${error.message || 'Erro desconhecido'}`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setSubmittingAppointment(false);
    }
  };

  const handleAppointmentInputChange = (field, value) => {
    setAppointmentForm(prev => ({
      ...prev,
      [field]: value,
      // Se desmarcar o transporte, limpar o preço
      ...(field === 'hasTransport' && !value ? { transportPrice: '' } : {})
    }));
  };

  // Função para excluir agendamento
  const handleDeleteAppointment = async (appointment) => {
    // Verificar se há transação vinculada do agregado
    const linkedTransaction = allTransactions.find(t => (t.idAgendamento || t.appointmentId) === appointment.id);
    const hasTransaction = !!linkedTransaction;

    const message = hasTransaction 
      ? `Tem certeza que deseja excluir o agendamento de ${appointment.nomePet || 'pet'} para ${appointment.tipoServico || 'serviço'}? A transação de faturamento vinculada (R$ ${appointment.servicePrice?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}) também será excluída. Esta ação não pode ser desfeita.`
      : `Tem certeza que deseja excluir o agendamento de ${appointment.nomePet || 'pet'} para ${appointment.tipoServico || 'serviço'}? Esta ação não pode ser desfeita.`;

    const confirmed = await showConfirmation({
      title: 'Excluir Agendamento',
      messidade: message,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      // Excluir transação vinculada ao agendamento
      if (hasTransaction && linkedTransaction) {
        try {
          const transactionData = linkedTransaction;
          await deleteDoc(doc(db, 'transacoes', linkedTransaction.id));
          
          // Atualizar agregado de transações (aguardar conclusão)
          try {
            await updateTransactionsAggregationOnDelete(linkedTransaction.id);
          } catch (err) {
                                        // Erro ao atualizar agregado de transações
          }
          
          // Recarregar agregado de transações após exclusão
          if (refreshTransactions) {
            await refreshTransactions();
          }
          
        } catch (transactionError) {
          throw transactionError;
        }
      }

      // Excluir o agendamento
      await deleteDoc(doc(db, 'agendamentos', appointment.id));

      // Atualizar agregado de agendamentos (aguardar conclusão)
      try {
        await updateAppointmentsAggregationOnDelete(appointment.id, appointment.dataAgendamento);
      } catch (err) {
        // Erro ao atualizar agregado de agendamentos
      }
      
      // Atualizar o estado local removendo o agendamento excluído (feedback imediato)
      setAppointments(prev => prev.filter(apt => apt.id !== appointment.id));
      
      // Recarregar agregado de agendamentos para garantir sincronização
      if (refreshAppointments) {
        await refreshAppointments();
      }
      
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        messidade: `Ocorreu um erro ao excluir o agendamento: ${error.message || 'Erro desconhecido'}. Por favor, tente novamente.`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  // Funções para gerenciar o plano mensal
  const toggleMonthlyPlan = () => {
    setFormData(prev => ({
      ...prev,
      planoMensal: !prev.planoMensal,
      diasAgendados: !prev.planoMensal ? [] : prev.diasAgendados
    }));
  };

  const addScheduledDay = async (day, time) => {
    try {
      const currentDays = pet.diasAgendados || [];
      const newDays = [...currentDays, { day, time }];
      
      await updateDoc(doc(db, 'pets', id), {
        diasAgendados: newDays,
        atualizadoEm: new Date()
      });
      
      const updatedPet = { ...pet, diasAgendados: newDays };
      setPet(updatedPet);
      setFormData(prev => ({ ...prev, diasAgendados: newDays }));
      setEditingDay(null);
      
      // Atualizar agendamentos recorrentes
      await updateRecurringAppointments(updatedPet);
      
      // Recarregar agregado de agendamentos para atualizar a lista
      if (refreshAppointments) {
        await refreshAppointments();
      }
    } catch (error) {
      // Erro ao adicionar horário
    }
  };

  const removeScheduledDay = async (index) => {
    try {
      const currentDays = pet.diasAgendados || [];
      const newDays = currentDays.filter((_, i) => i !== index);
      
      await updateDoc(doc(db, 'pets', id), {
        diasAgendados: newDays,
        atualizadoEm: new Date()
      });
      
      const updatedPet = { ...pet, diasAgendados: newDays };
      setPet(updatedPet);
      setFormData(prev => ({ ...prev, diasAgendados: newDays }));
      
      // Atualizar agendamentos recorrentes
      await updateRecurringAppointments(updatedPet);
      
      // Recarregar agregado de agendamentos para atualizar a lista
      if (refreshAppointments) {
        await refreshAppointments();
      }
    } catch (error) {
      // Erro ao remover horário
    }
  };

  const updateScheduledDay = async (index, day, time) => {
    try {
      const currentDays = pet.diasAgendados || [];
      const newDays = [...currentDays];
      newDays[index] = { day, time };
      
      await updateDoc(doc(db, 'pets', id), {
        diasAgendados: newDays,
        atualizadoEm: new Date()
      });
      
      const updatedPet = { ...pet, diasAgendados: newDays };
      setPet(updatedPet);
      setFormData(prev => ({ ...prev, diasAgendados: newDays }));
      setEditingDay(null);
      
      // Atualizar agendamentos recorrentes
      await updateRecurringAppointments(updatedPet);
      
      // Recarregar agregado de agendamentos para atualizar a lista
      if (refreshAppointments) {
        await refreshAppointments();
      }
    } catch (error) {
      // Erro ao atualizar horário
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const dataToSave = {
        nome: formData.nome.toUpperCase(),
        raca: formData.raca.toUpperCase(),
        mesNascimento: formData.mesNascimento,
        anoNascimento: formData.anoNascimento,
        porte: formData.porte,
        pelagem: formData.pelagem,
        tutor: formData.tutor.toUpperCase(),
        telefone: formData.telefone,
        endereco: formData.endereco,
        observacoes: formData.observacoes,
        fonteContato: formData.fonteContato || '',
        temAlergia: formData.temAlergia || false,
        detalhesAlergia: formData.detalhesAlergia || '',
        temDorArticular: formData.temDorArticular || false,
        detalhesDorArticular: formData.detalhesDorArticular || '',
        usaMedicamento: formData.usaMedicamento || false,
        detalhesMedicamento: formData.detalhesMedicamento || '',
        acompanhamentoVeterinario: formData.acompanhamentoVeterinario || false,
        detalhesAcompanhamento: formData.detalhesAcompanhamento || '',
        autorizaImagem: formData.autorizaImagem || false,
        planoMensal: formData.planoMensal,
        diasAgendados: formData.diasAgendados,
        intervaloRecorrente: formData.intervaloRecorrente || 'weekly',
        tipoServicoPlanoMensal: formData.tipoServicoPlanoMensal || '',
        precoPlanoMensal: formData.precoPlanoMensal || 0,
        dataInicioPlanoMensal: formData.dataInicioPlanoMensal || '',
        horaInicioPlanoMensal: formData.horaInicioPlanoMensal || '',
        atualizadoEm: Timestamp.now()
      };
      await updateDoc(doc(db, 'pets', id), dataToSave);
      
      // Otimização: usar dados que já temos ao invés de ler do Firestore
      const updatedPetData = {
        ...pet,
        ...dataToSave,
        id: pet.id
      };
      
      // Atualizar agregado com dados que já temos
      try {
        await updatePetsAggregationOnUpdate(updatedPetData);
      } catch (aggError) {
        // Erro ao atualizar agregado na edição
      }
      
      setPet(updatedPetData);
      
      setIsEditing(false);
    } catch (e) {
      // Erro ao salvar alterações
      setError('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirmation({
      title: 'Excluir Pet',
      messidade: 'Tem certeza que deseja excluir este pet? Esta ação não pode ser desfeita e todos os agendamentos relacionados serão excluídos.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      // Buscar todos os agendamentos relacionados ao pet
      const appointmentsQuery = query(
        collection(db, 'agendamentos'),
        where('idPet', '==', id)
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      
      // Deletar todos os agendamentos relacionados
      const deletePromises = [];
      appointmentsSnapshot.forEach((appointmentDoc) => {
        deletePromises.push(deleteDoc(doc(db, 'agendamentos', appointmentDoc.id)));
      });
      
      // Aguardar todas as exclusões de agendamentos
      await Promise.all(deletePromises);
      
      // Deletar o pet
      await deleteDoc(doc(db, 'pets', id));
      
      // Atualizar agregado
      try {
        await updatePetsAggregationOnDelete(id);
      } catch (aggError) {
        // Erro ao atualizar agregado na exclusão
      }
      
      navigate('/pets');
    } catch (e) {
      // Erro ao excluir pet
      setError('Erro ao excluir pet');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(-1)} className="btn-secondary flex items-center space-x-2">
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar</span>
        </button>
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="btn-secondary flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">
          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span>Voltar</span>
        </button>
        <div className="flex items-center space-x-2 sm:space-x-3">
          {!isEditing && (
            <button onClick={() => setIsEditing(true)} className="btn-secondary flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">
              <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Editar</span>
            </button>
          )}
          <button onClick={handleDelete} className="btn-danger flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">
            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Excluir</span>
          </button>
        </div>
      </div>

      <div className="card space-y-4 sm:space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between relative">
          <div className="flex-1">
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <Dog className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => {
                    // Permite apenas letras, espaços e caracteres acentuados
                    const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                    setFormData({ ...formData, nome: processedValue });
                  }}
                  className="input-field text-lg sm:text-xl font-bold uppercase"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
            ) : (
              <>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
                <Dog className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                <span className="uppercase">{pet.nome}</span>
              </h1>
              </>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-3 sm:mt-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Raça</label>
              {isEditing ? (
                <input
                  type="text"
                    value={formData.raca}
                    onChange={(e) => {
                      // Permite apenas letras, espaços e caracteres acentuados
                      const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                      setFormData({ ...formData, raca: processedValue });
                    }}
                  className="input-field text-sm sm:text-base uppercase"
                  style={{ textTransform: 'uppercase' }}
                />
              ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark uppercase">{pet.raca || 'Não informada'}</p>
              )}
            </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Idade</label>
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={formData.mesNascimento}
                      onChange={(e) => setFormData({ ...formData, mesNascimento: e.target.value })}
                      className="input-field text-sm sm:text-base"
                    >
                      <option value="">Mês</option>
                      <option value="Janeiro">Janeiro</option>
                      <option value="Fevereiro">Fevereiro</option>
                      <option value="Março">Março</option>
                      <option value="Abril">Abril</option>
                      <option value="Maio">Maio</option>
                      <option value="Junho">Junho</option>
                      <option value="Julho">Julho</option>
                      <option value="Agosto">Agosto</option>
                      <option value="Setembro">Setembro</option>
                      <option value="Outubro">Outubro</option>
                      <option value="Novembro">Novembro</option>
                      <option value="Dezembro">Dezembro</option>
                    </select>
                    <select
                      value={formData.anoNascimento}
                      onChange={(e) => setFormData({ ...formData, anoNascimento: e.target.value })}
                      className="input-field text-sm sm:text-base"
                    >
                      <option value="">Ano</option>
                      {Array.from({ length: 20 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{calculateCurrentAge(pet)}</p>
                )}
            </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Porte</label>
                {isEditing ? (
                  <select
                    value={formData.porte}
                    onChange={(e) => setFormData({ ...formData, porte: e.target.value })}
                    className="input-field text-sm sm:text-base"
                  >
                    <option value="">Selecione o porte</option>
                    <option value="mini">Mini - até 4 kg</option>
                    <option value="pequeno">Pequeno - entre 4 kg e 10 kg</option>
                    <option value="medio">Médio - entre 10 kg e 25 kg</option>
                    <option value="grande">Grande - entre 25 kg e 45 kg</option>
                    <option value="gigante">Gigante - acima de 45 kg</option>
                  </select>
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{pet.porte ? getSizeLabel(pet.porte) : 'Não informado'}</p>
                )}
            </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Pelagem</label>
                {isEditing ? (
                  <select
                    value={formData.pelagem}
                    onChange={(e) => setFormData({ ...formData, pelagem: e.target.value })}
                    className="input-field text-sm sm:text-base"
                  >
                    <option value="">Selecione a pelagem</option>
                    <option value="curto">Curto</option>
                    <option value="medio">Médio</option>
                    <option value="longo">Longo</option>
                  </select>
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{pet.pelagem ? getCoatLabel(pet.pelagem) : 'Não informada'}</p>
                )}
            </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Tutor</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.tutor}
                    onChange={(e) => {
                      // Permite apenas letras, espaços e caracteres acentuados
                      const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                      setFormData({ ...formData, tutor: processedValue });
                    }}
                    className="input-field text-sm sm:text-base uppercase"
                    style={{ textTransform: 'uppercase' }}
                  />
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{formatName(pet.tutor) || 'Não informado'}</p>
                )}
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Telefone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formatPhone(formData.telefone)}
                    onChange={(e) => {
                      // Remove tudo que não é número e limita a 11 dígitos
                      const numbers = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setFormData({ ...formData, telefone: numbers });
                    }}
                    className="input-field text-sm sm:text-base"
                    placeholder="Ex: (41) 99999-9999"
                  />
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{pet.telefone ? formatPhone(pet.telefone) : 'Não informado'}</p>
                )}
                </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Endereço</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    className="input-field text-sm sm:text-base"
                  />
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark flex items-center space-x-1">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                    <span>{pet.endereco || 'Não informado'}</span>
                  </p>
                )}
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Como nos conheceu?</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.fonteContato}
                    onChange={(e) => setFormData({ ...formData, fonteContato: e.target.value })}
                    className="input-field text-sm sm:text-base"
                    placeholder="Ex: Instagram, indicação, etc."
                  />
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{pet.fonteContato || 'Não informado'}</p>
                )}
              </div>
              {/* Informações de Saúde */}
              <div className="space-y-1.5 sm:space-y-2 col-span-2 md:col-span-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark mb-2 block">Informações de Saúde do Pet:</label>
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.temAlergia}
                          onChange={(e) => setFormData({ ...formData, temAlergia: e.target.checked, detalhesAlergia: e.target.checked ? formData.detalhesAlergia: '' })}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">Tem alergia</span>
                      </label>
                      {formData.temAlergia && (
                        <input
                          type="text"
                          value={formData.detalhesAlergia}
                          onChange={(e) => setFormData({ ...formData, detalhesAlergia: e.target.value })}
                          required
                          className="input-field text-sm sm:text-base"
                          placeholder="Descreva a alergia específica..."
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.temDorArticular}
                          onChange={(e) => setFormData({ ...formData, temDorArticular: e.target.checked, detalhesDorArticular: e.target.checked ? formData.detalhesDorArticular: '' })}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">Tem dor articular</span>
                      </label>
                      {formData.temDorArticular && (
                        <input
                          type="text"
                          value={formData.detalhesDorArticular}
                          onChange={(e) => setFormData({ ...formData, detalhesDorArticular: e.target.value })}
                          required
                          className="input-field text-sm sm:text-base"
                          placeholder="Descreva o problema articular específico..."
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.usaMedicamento}
                          onChange={(e) => setFormData({ ...formData, usaMedicamento: e.target.checked, detalhesMedicamento: e.target.checked ? formData.detalhesMedicamento: '' })}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">Usa medicamento</span>
                      </label>
                      {formData.usaMedicamento && (
                        <input
                          type="text"
                          value={formData.detalhesMedicamento}
                          onChange={(e) => setFormData({ ...formData, detalhesMedicamento: e.target.value })}
                          required
                          className="input-field text-sm sm:text-base"
                          placeholder="Descreva o medicamento usado..."
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.acompanhamentoVeterinario}
                          onChange={(e) => setFormData({ ...formData, acompanhamentoVeterinario: e.target.checked, detalhesAcompanhamento: e.target.checked ? formData.detalhesAcompanhamento: '' })}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">Está fazendo acompanhamento veterinário</span>
                      </label>
                      {formData.acompanhamentoVeterinario && (
                        <input
                          type="text"
                          value={formData.detalhesAcompanhamento}
                          onChange={(e) => setFormData({ ...formData, detalhesAcompanhamento: e.target.value })}
                          required
                          className="input-field text-sm sm:text-base"
                          placeholder="Descreva o tipo de acompanhamento..."
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div>
                      <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                        {pet.temAlergia ? (
                          <span><span className="text-red-600 dark:text-red-400">✓</span> Tem alergia{pet.detalhesAlergia ? `: ${pet.detalhesAlergia}` : ''}</span>
                        ) : (
                          <span>✗ Não possui alergias conhecidas</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                        {pet.temDorArticular ? (
                          <span><span className="text-red-600 dark:text-red-400">✓</span> Tem dor articular{pet.detalhesDorArticular ? `: ${pet.detalhesDorArticular}` : ''}</span>
                        ) : (
                          <span>✗ Não possui dores articulares</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                        {pet.usaMedicamento ? (
                          <span><span className="text-red-600 dark:text-red-400">✓</span> Usa medicamento{pet.detalhesMedicamento ? `: ${pet.detalhesMedicamento}` : ''}</span>
                        ) : (
                          <span>✗ Não usa medicamentos</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                        {pet.acompanhamentoVeterinario ? (
                          <span><span className="text-red-600 dark:text-red-400">✓</span> Está fazendo acompanhamento veterinário{pet.detalhesAcompanhamento ? `: ${pet.detalhesAcompanhamento}` : ''}</span>
                        ) : (
                          <span>✗ Não está fazendo acompanhamento veterinário</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Autorização de Imagem e Observações */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Autorização de Imagem</label>
                {isEditing ? (
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.autorizaImagem}
                      onChange={(e) => setFormData({ ...formData, autorizaImagem: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">Autorizo o uso de imagem do pet nas redes sociais</span>
                  </label>
                ) : (
                  <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                    {pet.autorizaImagem ? (
                      <span><span className="text-green-600 dark:text-green-400">✓</span> Autorizado</span>
                    ) : (
                      <span><span className="text-red-600 dark:text-red-400">✗</span> Não autorizado</span>
                    )}
                  </p>
                )}
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark">Observações</label>
                {isEditing ? (
                  <textarea
                    ref={notesTextareaRef}
                    value={formData.observacoes}
                    onChange={(e) => {
                      setFormData({ ...formData, observacoes: e.target.value });
                      // Ajustar altura automaticamente
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.max(2.625 * 16, e.target.scrollHeight)}px`;
                    }}
                    onInput={(e) => {
                      // Ajustar altura automaticamente ao digitar
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.max(2.625 * 16, e.target.scrollHeight)}px`;
                    }}
                    className="input-field w-full text-sm sm:text-base"
                    rows={1}
                    style={{ reporte: 'none', minHeight: '2.625rem', overflow: 'hidden' }}
                  />
                ) : (
                  <p 
                    className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark min-h-[2.625rem] rounded-lg bg-card-light dark:bg-card-dark"
                    style={{ 
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word', 
                      margin: 0, 
                      padding: '0.5rem 0',
                      lineHeight: '1.25rem'
                    }}
                  >
                    {pet.observacoes || 'Nenhuma observação'}
                  </p>
                )}
              </div>
            </div>

            {/* Botões de Salvar e Cancelar - Modo Edição */}
            {isEditing && (
              <div className="mt-4 sm:mt-6 flex space-x-2 sm:space-x-3">
                <button onClick={() => setIsEditing(false)} className="flex-1 btn-secondary text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2">Cancelar</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2">
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            )}

            {/* Plano Mensal */}
            <div className="mt-4 sm:mt-6 border-t border-border-light dark:border-border-dark pt-4 sm:pt-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                  <label className="text-xs sm:text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Pet possui plano de banhos mensal?
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pet?.planoMensal || false}
                      onChange={async () => {
                        try {
                          const newValue = !pet.planoMensal;
                          
                          // Mostrar confirmação
                          const confirmed = await showConfirmation({
                            title: newValue ? 'Ativar Plano Mensal' : 'Desativar Plano Mensal',
                            messidade: newValue 
                              ? `Ativar o plano mensal para ${pet.nome}? Os agendamentos recorrentes serão gerados automaticamente.`
                              : `Desativar o plano mensal para ${pet.nome}? Os agendamentos recorrentes futuros serão cancelados.`,
                            confirmText: newValue ? 'Sim, Ativar' : 'Sim, Desativar',
                            cancelText: 'Cancelar',
                            type: 'warning'
                          });
                          
                          if (!confirmed) return;
                          
                          await updateDoc(doc(db, 'pets', id), {
                            planoMensal: newValue,
                            diasAgendados: newValue ? pet.diasAgendados || [] : pet.diasAgendados || [],
                            intervaloRecorrente: newValue ? (pet.intervaloRecorrente || 'weekly') : (pet.intervaloRecorrente || 'weekly'),
                            atualizadoEm: Timestamp.now()
                          });
                          
                          // Otimização: usar dados que já temos ao invés de ler do Firestore
                          const updatedPetData = {
                            ...pet,
                            planoMensal: newValue,
                            diasAgendados: newValue ? pet.diasAgendados || [] : pet.diasAgendados || [],
                            intervaloRecorrente: newValue ? (pet.intervaloRecorrente || 'weekly') : (pet.intervaloRecorrente || 'weekly'),
                            atualizadoEm: Timestamp.now()
                          };
                          
                          // Atualizar agregado de pets
                          try {
                            await updatePetsAggregationOnUpdate(updatedPetData);
                          } catch (aggError) {
                            // Erro ao atualizar agregado de pets
                          }
                          
                          setPet(updatedPetData);
                          setFormData(prev => ({ ...prev, planoMensal: newValue }));
                          
                          // Se ativou o plano, entrar em modo de edição e inicializar campos
                          if (newValue) {
                            const interval = pet?.intervaloRecorrente || 'weekly';
                            const numberOfFields = interval === 'biweekly' ? 2 : 4;
                            const daysToShow = [];
                            for (let i = 0; i < numberOfFields; i++) {
                              daysToShow.push({ date: '', time: '' });
                            }
                            setFormData(prev => ({ ...prev, diasAgendados: daysToShow, dataInicioPlanoMensal: '', horaInicioPlanoMensal: '' }));
                            setEditingMonthlyPlan(true);
                          } else {
                            setEditingMonthlyPlan(false);
                          }
                          
                          // Não gerar agendamentos aqui - serão gerados ao salvar
                        } catch (error) {
                          // Erro ao atualizar plano mensal
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                  </label>
                </div>
                
                {/* Botões de Editar/Salvar/Cancelar */}
                {(pet?.planoMensal || formData.planoMensal) && (
                  <div className="flex items-center space-x-1.5 sm:space-x-2">
                    {!editingMonthlyPlan ? (
                      <button
                        onClick={() => {
                          const interval = pet?.intervaloRecorrente || formData.intervaloRecorrente || 'weekly';
                          const numberOfFields = interval === 'biweekly' ? 2 : 4;
                          const currentDays = pet?.diasAgendados || formData.diasAgendados || [];
                          // Garantir que temos o número correto de campos ao entrar em edição
                          const daysToShow = [];
                          for (let i = 0; i < numberOfFields; i++) {
                            if (currentDays[i]) {
                              // Converter de formato antigo (day) para novo (date) se necessário
                              const day = currentDays[i];
                              daysToShow.push({ 
                                date: day.date || '', 
                                time: day.time || '' 
                              });
                            } else {
                              daysToShow.push({ date: '', time: '' });
                            }
                          }
                          setFormData(prev => ({ ...prev, diasAgendados: daysToShow, dataInicioPlanoMensal: pet?.dataInicioPlanoMensal || pet?.planoMensalStartDate || formData.dataInicioPlanoMensal || formData.planoMensalStartDate || '', horaInicioPlanoMensal: pet?.horaInicioPlanoMensal || pet?.planoMensalStartTime || formData.horaInicioPlanoMensal || formData.planoMensalStartTime || '' }));
                          setEditingMonthlyPlan(true);
                        }}
                        className="btn-secondary flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2"
                      >
                        <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span>Editar</span>
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingMonthlyPlan(false);
                            // Reverter para valores do pet
                            setFormData(prev => ({
                              ...prev,
                              tipoServicoPlanoMensal: pet?.tipoServicoPlanoMensal || pet?.planoMensalServiceType || '',
                              precoPlanoMensal: pet?.precoPlanoMensal || pet?.planoMensalPrice || 0,
                              intervaloRecorrente: pet?.intervaloRecorrente || 'weekly',
                              diasAgendados: pet?.diasAgendados || [],
                              dataInicioPlanoMensal: pet?.dataInicioPlanoMensal || pet?.planoMensalStartDate || '',
                              horaInicioPlanoMensal: pet?.horaInicioPlanoMensal || pet?.planoMensalStartTime || ''
                            }));
                            setPlanValidationErrors({});
                          }}
                          className="btn-secondary text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={async () => {
                            // Validar todos os campos obrigatórios do plano mensal
                            const missingFields = [];
                            
                            if (!formData.tipoServicoPlanoMensal || formData.tipoServicoPlanoMensal.trim() === '') {
                              missingFields.push('Tipo de Serviço');
                            }
                            
                            if (!formData.precoPlanoMensal || formData.precoPlanoMensal <= 0) {
                              missingFields.push('Valor do Plano Mensal');
                            }
                            
                            if (!formData.dataInicioPlanoMensal || formData.dataInicioPlanoMensal.trim() === '') {
                              missingFields.push('Data de Início');
                            }
                            
                            if (!formData.horaInicioPlanoMensal || formData.horaInicioPlanoMensal.trim() === '') {
                              missingFields.push('Horário de Início');
                            }
                            
                            // Validar que todos os dias agendados tenham data e hora preenchidas
                            const diasAgendados = formData.diasAgendados || [];
                            const diasIncompletos = diasAgendados.filter(day => !day.date || !day.time || day.date.trim() === '' || day.time.trim() === '');
                            
                            if (diasIncompletos.length > 0) {
                              missingFields.push('Agendamentos (todos os dias devem ter data e horário preenchidos)');
                            }
                            
                            if (missingFields.length > 0) {
                              // Marcar campos com erro para destacar visualmente
                              const errors = {};
                              if (!formData.tipoServicoPlanoMensal || formData.tipoServicoPlanoMensal.trim() === '') {
                                errors.tipoServicoPlanoMensal = true;
                              }
                              if (!formData.precoPlanoMensal || formData.precoPlanoMensal <= 0) {
                                errors.precoPlanoMensal = true;
                              }
                              if (!formData.dataInicioPlanoMensal || formData.dataInicioPlanoMensal.trim() === '') {
                                errors.dataInicioPlanoMensal = true;
                              }
                              if (!formData.horaInicioPlanoMensal || formData.horaInicioPlanoMensal.trim() === '') {
                                errors.horaInicioPlanoMensal = true;
                              }
                              // Marcar campos individuais de dias agendados que estão faltando
                              const diasAgendados = formData.diasAgendados || [];
                              diasAgendados.forEach((day, index) => {
                                if (!day.date || day.date.trim() === '') {
                                  errors[`diasAgendados_${index}_date`] = true;
                                }
                                if (!day.time || day.time.trim() === '') {
                                  errors[`diasAgendados_${index}_time`] = true;
                                }
                              });
                              
                              setPlanValidationErrors(errors);
                              
                              await showConfirmation({
                                title: 'Campos Obrigatórios',
                                message: `Por favor, preencha todos os campos do plano antes de salvar.`,
                                confirmText: 'OK',
                                showCancel: false,
                                type: 'warning'
                              });
                              
                              // Fazer scroll para o primeiro campo com erro após fechar o modal
                              setTimeout(() => {
                                if (errors.tipoServicoPlanoMensal) {
                                  document.querySelector('[data-field="tipoServicoPlanoMensal"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                } else if (errors.precoPlanoMensal) {
                                  document.querySelector('[data-field="precoPlanoMensal"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                } else if (errors.dataInicioPlanoMensal) {
                                  document.querySelector('[data-field="dataInicioPlanoMensal"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                } else if (errors.horaInicioPlanoMensal) {
                                  document.querySelector('[data-field="horaInicioPlanoMensal"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                } else {
                                  // Fazer scroll para o primeiro campo de dia agendado com erro
                                  const firstErrorField = document.querySelector('[data-field^="diasAgendados_"]');
                                  if (firstErrorField) {
                                    firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }
                                }
                              }, 100);
                              
                              return;
                            }
                            
                            // Limpar erros se todos os campos estão preenchidos
                            setPlanValidationErrors({});
                            
                            setSavingPlan(true);
                            try {
                              const dataToSave = {
                                tipoServicoPlanoMensal: formData.tipoServicoPlanoMensal || '',
                                precoPlanoMensal: formData.precoPlanoMensal || 0,
                                intervaloRecorrente: formData.intervaloRecorrente || 'weekly',
                                diasAgendados: formData.diasAgendados || [],
                                dataInicioPlanoMensal: formData.dataInicioPlanoMensal || '',
                                horaInicioPlanoMensal: formData.horaInicioPlanoMensal || '',
                                atualizadoEm: new Date()
                              };
                              
                              await updateDoc(doc(db, 'pets', id), {
                                ...dataToSave,
                                atualizadoEm: Timestamp.now()
                              });
                              
                              // Otimização: usar dados que já temos ao invés de ler do Firestore
                              const updatedPet = {
                                ...pet,
                                ...dataToSave,
                                id: pet.id,
                                planoMensal: formData.planoMensal || pet.planoMensal || false,
                                atualizadoEm: Timestamp.now()
                              };
                              
                              // Atualizar agregado de pets
                              try {
                                await updatePetsAggregationOnUpdate(updatedPet);
                              } catch (aggError) {
                                // Erro ao atualizar agregado de pets
                              }
                              
                              setPet(updatedPet);
                              
                              // Criar transação de faturamento se o plano está ativo e há valor
                              const planPrice = formData.precoPlanoMensal || 0;
                              const previousPrice = pet.precoPlanoMensal || pet.planoMensalPrice || 0;
                              const planIsActive = updatedPet.planoMensal;
                              
                              // Verificar se já existe uma transação para este plano na data exata (evitar duplicatas)
                              let shouldCreateTransaction = false;
                              
                              if (planIsActive && planPrice > 0) {
                                const now = new Date();
                                const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                
                                // Buscar transações de plano mensal para este pet do agregado
                                const petTransactions = allTransactions.filter(t => 
                                  t.idPet === pet.id && 
                                  t.descricao && 
                                  (t.descricao.includes(`Faturamento do plano de ${pet.nome}`) || 
                                   t.descricao.includes(`Faturamento do plano mensal de ${pet.nome}`))
                                );
                                
                                // Verificar se já existe transação para a data exata
                                let hasTransactionForCurrentDate = false;
                                let existingTransactionForDate = null;
                                
                                petTransactions.forEach(transaction => {
                                  if (transaction.date) {
                                    const transactionDate = transaction.date instanceof Date 
                                      ? transaction.date 
                                      : (transaction.date.toDate ? transaction.date.toDate() : new Date(transaction.date));
                                    const transactionDateOnly = new Date(
                                      transactionDate.getFullYear(),
                                      transactionDate.getMonth(),
                                      transactionDate.getDate()
                                    );
                                    
                                    // Comparar apenas dia, mês e ano (ignorar hora)
                                    if (transactionDateOnly.getTime() === currentDate.getTime()) {
                                      hasTransactionForCurrentDate = true;
                                      existingTransactionForDate = { 
                                        id: transaction.id, 
                                        data: transaction 
                                      };
                                    }
                                  }
                                });
                                
                                // Criar transação se não existe para a data exata
                                if (!hasTransactionForCurrentDate) {
                                  shouldCreateTransaction = true;
                                } else if (existingTransactionForDate && (existingTransactionForDate.data.valor || existingTransactionForDate.data.amount) !== planPrice) {
                                  // Se existe mas o valor mudou, atualizar a transação existente
                                  try {
                                    const oldTransactionData = existingTransactionForDate.data;
                                    const oldTransactionDate = oldTransactionData.data || oldTransactionData.date;
                                    await updateDoc(doc(db, 'transacoes', existingTransactionForDate.id), {
                                      valor: planPrice,
                                      nomeTipoTransacao: 'Agendamentos',
                                      atualizadoEm: Timestamp.fromDate(now)
                                    });
                                    
                                  // Otimização: usar dados que já temos ao invés de ler do Firestore
                                  const nomePet = pet?.nome || null;
                                  
                                  const updatedTransactionData = {
                                    ...existingTransactionForDate,
                                    valor: planPrice,
                                    atualizadoEm: Timestamp.fromDate(now),
                                    nomePet: nomePet,
                                    nomeTipoTransacao: 'Agendamentos'
                                  };
                                    
                                    // Atualizar agregado de transações
                                    try {
                                      await updateTransactionsAggregationOnUpdate(oldTransactionData, updatedTransactionData);
                                    } catch (err) {
                                      // Erro ao atualizar agregado de transações
                                    }
                                    
                                    // Recarregar agregado de transações após atualização
                                    if (refreshTransactions) {
                                      await refreshTransactions();
                                    }
                                  } catch (error) {
                                    // Erro ao atualizar transação do plano
                                  }
                                }
                              }
                              
                              if (shouldCreateTransaction) {
                                try {
                                  // Criar transação de forma mais direta
                                  const now = new Date();
                                  const dateTimestamp = Timestamp.fromDate(now);
                                  const createdAtTimestamp = Timestamp.fromDate(now);
                                  
                                  let createdByData = null;
                                  try {
                                    if (user) {
                                      createdByData = {
                                        uid: user.uid,
                                        nomeExibicao: user.nomeExibicao || 'Usuário'
                                      };
                                    }
                                  } catch (createdByError) {
                                    // Erro ao criar createdByData
                                  }
                                  
                                  const transactionData = {
                                    tipo: 'receita',
                                    idPet: pet.id,
                                    valor: planPrice,
                                    idTipoTransacao: '',
                                    nomeTipoTransacao: 'Agendamentos',
                                    descricao: `Faturamento do plano mensal de ${pet.nome}`,
                                    data: dateTimestamp,
                                    criadoEm: createdAtTimestamp,
                                    criadoPor: createdByData
                                  };
                                  
                                  const transactionsCollection = collection(db, 'transacoes');
                                  const transactionDocRef = await addDoc(transactionsCollection, transactionData);
                                  
                                  // Otimização: usar dados que já temos ao invés de ler do Firestore
                                  const nomePet = pet?.nome || null;
                                  
                                  const newTransactionData = {
                                    id: transactionDocRef.id,
                                    tipo: 'receita',
                                    idPet: pet.id,
                                    valor: planPrice,
                                    idTipoTransacao: '',
                                    nomeTipoTransacao: 'Agendamentos',
                                    descricao: `Faturamento do plano mensal de ${pet.nome}`,
                                    data: dateTimestamp,
                                    criadoEm: createdAtTimestamp,
                                    criadoPor: createdByData,
                                    nomePet: nomePet,
                                    idAgendamento: null
                                  };
                                  
                                  // Atualizar agregado de transações
                                  try {
                                    await updateTransactionsAggregationOnCreate(newTransactionData);
                                  } catch (err) {
                                    // Erro ao atualizar agregado de transações
                                  }
                                  
                                  // Recarregar agregado de transações após criação
                                  if (refreshTransactions) {
                                    await refreshTransactions();
                                  }
                                } catch (error) {
                                  // Erro ao criar transação do plano
                                }
                              }
                              
                              // Gerar/atualizar agendamentos se o plano está ativo e há dias agendados
                              // Garantir que updatedPet tenha todos os campos necessários do formData
                              const petForAppointments = {
                                ...updatedPet,
                                diasAgendados: formData.diasAgendados || updatedPet.diasAgendados || [],
                                tipoServicoPlanoMensal: formData.tipoServicoPlanoMensal || updatedPet.tipoServicoPlanoMensal || updatedPet.planoMensalServiceType || '',
                                precoPlanoMensal: formData.precoPlanoMensal || updatedPet.precoPlanoMensal || updatedPet.planoMensalPrice || 0,
                                intervaloRecorrente: formData.intervaloRecorrente || updatedPet.intervaloRecorrente || 'weekly'
                              };
                              
                              if (petForAppointments.planoMensal && petForAppointments.diasAgendados && petForAppointments.diasAgendados.length > 0) {
                                // Verificar se os diasAgendados têm dados válidos (data e hora)
                                const validDays = petForAppointments.diasAgendados.filter(day => day.date && day.time);
                                
                                if (validDays.length > 0) {
                                  const intervalChanged = (pet.intervaloRecorrente || 'weekly') !== (formData.intervaloRecorrente || 'weekly');
                                  const daysChanged = JSON.stringify(pet.diasAgendados || []) !== JSON.stringify(formData.diasAgendados || []);
                                  const planWasJustActivated = !pet.planoMensal && updatedPet.planoMensal;
                                  const hadNoDays = !pet.diasAgendados || pet.diasAgendados.length === 0;
                                  
                                  // Sempre gerar/atualizar agendamentos quando há dias válidos
                                  if (planWasJustActivated || hadNoDays || intervalChanged || daysChanged) {
                                    // Se houve mudanças significativas, atualizar agendamentos
                                    if (intervalChanged || daysChanged) {
                                      await updateRecurringAppointments(petForAppointments);
                                    } else {
                                      // Se foi ativado ou dias foram adicionados, gerar novos
                                      await generateMonthlyAppointments(petForAppointments);
                                    }
                                  } else {
                                    // Mesmo sem mudanças detectadas, verificar se precisa gerar agendamentos
                                    await generateMonthlyAppointments(petForAppointments);
                                  }
                                  
                                  // Recarregar agregado de agendamentos para atualizar a lista
                                  if (refreshAppointments) {
                                    await refreshAppointments();
                                  }
                                }
                                
                              } else if (!updatedPet.planoMensal && pet.planoMensal) {
                                // Se o plano foi desativado, atualizar agendamentos existentes
                                await updateRecurringAppointments(updatedPet);
                                
                                // Recarregar agregado de agendamentos para atualizar a lista
                                if (refreshAppointments) {
                                  await refreshAppointments();
                                }
                              }
                              
                              setEditingMonthlyPlan(false);
                              setPlanValidationErrors({});
                            } catch (error) {
                              // Erro ao salvar plano mensal
                              await showConfirmation({
                                title: 'Erro',
                                messidade: `Ocorreu um erro ao salvar o plano: ${error.message || 'Erro desconhecido'}. Por favor, tente novamente.`,
                                confirmText: 'OK',
                                showCancel: false,
                                type: 'danger'
                              });
                            } finally {
                              setSavingPlan(false);
                            }
                          }}
                          disabled={savingPlan}
                          className="btn-primary text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingPlan ? 'Salvando...' : 'Salvar'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {(pet?.planoMensal || formData.planoMensal) && (
                <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                  {/* Tipo de serviço, valor do plano e frequência na mesma linha */}
                  <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-background-light dark:bg-background-dark rounded-lg border border-border-light dark:border-border-dark">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                      {/* Campo para definir o tipo de serviço do plano mensal */}
                      <div className="flex flex-col">
                        <label className="text-xs sm:text-sm font-medium text-foreground-light dark:text-foreground-dark mb-2 sm:mb-2 block">
                          Tipo de Serviço do Plano:
                        </label>
                        {editingMonthlyPlan ? (
                          <>
                            <input
                              type="text"
                              data-field="tipoServicoPlanoMensal"
                              value={formData.tipoServicoPlanoMensal || ''}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, tipoServicoPlanoMensal: e.target.value }));
                                // Limpar erro quando começar a preencher
                                if (planValidationErrors.tipoServicoPlanoMensal) {
                                  setPlanValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.tipoServicoPlanoMensal;
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark text-sm sm:text-base ${
                                planValidationErrors.tipoServicoPlanoMensal 
                                  ? 'border-red-500 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-900' 
                                  : 'border-border-light dark:border-border-dark'
                              }`}
                              placeholder="Ex: Banho + Tosa"
                            />
                          </>
                        ) : (
                          <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                            {pet?.tipoServicoPlanoMensal || pet?.planoMensalServiceType || formData.tipoServicoPlanoMensal || formData.planoMensalServiceType || 'Não definido'}
                          </p>
                        )}
                      </div>
                      
                      {/* Campo para definir o valor do plano mensal */}
                      <div className="flex flex-col">
                        <label className="text-xs sm:text-sm font-medium text-foreground-light dark:text-foreground-dark mb-2 sm:mb-2 block">
                          Valor do Plano Mensal (R$):
                        </label>
                        {editingMonthlyPlan ? (
                          <>
                            <input
                              type="number"
                              data-field="precoPlanoMensal"
                              step="0.01"
                              min="0"
                              value={formData.precoPlanoMensal || 0}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, precoPlanoMensal: parseFloat(e.target.value) || 0 }));
                                // Limpar erro quando começar a preencher
                                if (planValidationErrors.precoPlanoMensal) {
                                  setPlanValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.precoPlanoMensal;
                                    return newErrors;
                                  });
                                }
                              }}
                              className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark text-sm sm:text-base ${
                                planValidationErrors.precoPlanoMensal 
                                  ? 'border-red-500 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-900' 
                                  : 'border-border-light dark:border-border-dark'
                              }`}
                              placeholder="0.00"
                            />
                          </>
                        ) : (
                          <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                            R$ {(pet?.precoPlanoMensal || pet?.planoMensalPrice || formData.precoPlanoMensal || formData.planoMensalPrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        )}
                      </div>

                      {/* Seleção de intervalo de recorrência */}
                      <div className="flex flex-col">
                        <label className="text-xs sm:text-sm font-medium text-foreground-light dark:text-foreground-dark mb-2 sm:mb-2 block">
                          Frequência dos agendamentos:
                        </label>
                        {editingMonthlyPlan ? (
                          <div className="flex space-x-3 sm:space-x-4">
                            <label className="flex items-center space-x-1.5 sm:space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="recurringInterval"
                                value="weekly"
                                checked={(formData.intervaloRecorrente || 'weekly') === 'weekly'}
                                onChange={(e) => {
                                  const interval = e.target.value;
                                  // Criar 4 campos para semanal
                                  const numberOfFields = 4;
                                  const currentDays = formData.diasAgendados || [];
                                  const newDays = [];
                                  for (let i = 0; i < numberOfFields; i++) {
                                    if (currentDays[i]) {
                                      newDays.push({ ...currentDays[i], date: currentDays[i].date || '' });
                                    } else {
                                      newDays.push({ date: '', time: '' });
                                    }
                                  }
                                  setFormData(prev => ({ ...prev, intervaloRecorrente: interval, diasAgendados: newDays }));
                                }}
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">Semanal</span>
                            </label>
                            <label className="flex items-center space-x-1.5 sm:space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="recurringInterval"
                                value="biweekly"
                                checked={(formData.intervaloRecorrente || 'weekly') === 'biweekly'}
                                onChange={(e) => {
                                  const interval = e.target.value;
                                  // Criar 2 campos para a cada 2 semanas
                                  const numberOfFields = 2;
                                  const currentDays = formData.diasAgendados || [];
                                  const newDays = [];
                                  for (let i = 0; i < numberOfFields; i++) {
                                    if (currentDays[i]) {
                                      newDays.push({ ...currentDays[i], date: currentDays[i].date || '' });
                                    } else {
                                      newDays.push({ date: '', time: '' });
                                    }
                                  }
                                  setFormData(prev => ({ ...prev, intervaloRecorrente: interval, diasAgendados: newDays }));
                                }}
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">A cada 2 semanas</span>
                            </label>
                          </div>
                        ) : (
                          <p className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                            {(pet?.intervaloRecorrente || formData.intervaloRecorrente || 'weekly') === 'biweekly' ? 'A cada 2 semanas' : 'Semanal'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Horários agendados */}
                  <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-background-light dark:bg-background-dark rounded-lg border border-border-light dark:border-border-dark">
                    {editingMonthlyPlan ? (
                      <div className="space-y-2 sm:space-y-3">
                        {/* Campo de Data de Início na mesma linha do título */}
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <label className="text-xs sm:text-sm font-medium text-foreground-light dark:text-foreground-dark whitespace-nowrap">
                            Agendamentos do plano mensal - Data de Início:
                          </label>
                          <input
                            type="date"
                            data-field="dataInicioPlanoMensal"
                            value={formData.dataInicioPlanoMensal || ''}
                            onChange={(e) => {
                              const startDate = e.target.value;
                              // Limpar erro quando começar a preencher
                              if (planValidationErrors.dataInicioPlanoMensal) {
                                setPlanValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.dataInicioPlanoMensal;
                                  return newErrors;
                                });
                              }
                              const interval = formData.intervaloRecorrente || 'weekly';
                              const numberOfFields = interval === 'biweekly' ? 2 : 4;
                              
                              // Calcular datas automaticamente baseadas na data de início
                              const newDays = [];
                              const startTime = formData.horaInicioPlanoMensal || '';
                              if (startDate) {
                                // Parse da data como horário local (YYYY-MM-DD)
                                const [year, month, day] = startDate.split('-').map(Number);
                                const baseDate = new Date(year, month - 1, day);
                                // Determinar o intervalo em dias baseado no tipo de recorrência
                                const daysInterval = interval === 'biweekly' ? 14 : 7;
                                for (let i = 0; i < numberOfFields; i++) {
                                  const dateToSet = new Date(baseDate);
                                  dateToSet.setDate(baseDate.getDate() + (i * daysInterval)); // Adicionar i intervalos
                                  // Formatar como YYYY-MM-DD usando horário local
                                  const yearStr = dateToSet.getFullYear();
                                  const monthStr = String(dateToSet.getMonth() + 1).padStart(2, '0');
                                  const dayStr = String(dateToSet.getDate()).padStart(2, '0');
                                  const formattedDate = `${yearStr}-${monthStr}-${dayStr}`;
                                  // Usar o horário de início se houver, senão preservar o existente
                                  const timeToUse = startTime || formData.diasAgendados?.[i]?.time || '';
                                  newDays.push({ date: formattedDate, time: timeToUse });
                                }
                              } else {
                                // Se não houver data de início, manter os campos vazios mas preservar horários
                                for (let i = 0; i < numberOfFields; i++) {
                                  const timeToUse = startTime || formData.diasAgendados?.[i]?.time || '';
                                  newDays.push({ date: '', time: timeToUse });
                                }
                              }
                              
                              setFormData(prev => ({ ...prev, dataInicioPlanoMensal: startDate, diasAgendados: newDays }));
                            }}
                            className={`px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark text-xs sm:text-sm ${
                              planValidationErrors.dataInicioPlanoMensal 
                                ? 'border-red-500 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-900' 
                                : 'border-border-light dark:border-border-dark'
                            }`}
                            required
                          />
                          <input
                            type="time"
                            data-field="horaInicioPlanoMensal"
                            value={formData.horaInicioPlanoMensal || ''}
                            onChange={(e) => {
                              const startTime = e.target.value;
                              // Limpar erro quando começar a preencher
                              if (planValidationErrors.horaInicioPlanoMensal) {
                                setPlanValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.horaInicioPlanoMensal;
                                  return newErrors;
                                });
                              }
                              const interval = formData.intervaloRecorrente || 'weekly';
                              const numberOfFields = interval === 'biweekly' ? 2 : 4;
                              
                              // Copiar o horário para todos os campos de agendamento
                              const newDays = [];
                              const currentDays = formData.diasAgendados || [];
                              for (let i = 0; i < numberOfFields; i++) {
                                const existingDate = currentDays[i]?.date || '';
                                newDays.push({ date: existingDate, time: startTime });
                              }
                              
                              setFormData(prev => ({ ...prev, horaInicioPlanoMensal: startTime, diasAgendados: newDays }));
                            }}
                            className={`px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark text-xs sm:text-sm ${
                              planValidationErrors.horaInicioPlanoMensal 
                                ? 'border-red-500 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-900' 
                                : 'border-border-light dark:border-border-dark'
                            }`}
                            required
                          />
                        </div>
                        
                        {(() => {
                          const interval = formData.intervaloRecorrente || 'weekly';
                          const numberOfFields = interval === 'biweekly' ? 2 : 4;
                          const currentDays = formData.diasAgendados || [];
                          // Garantir que temos o número correto de campos
                          const daysToShow = [];
                          for (let i = 0; i < numberOfFields; i++) {
                            if (currentDays[i] && currentDays[i].date) {
                              // Usar apenas se tiver data (novo formato), ignorar formato antigo com "day"
                              daysToShow.push({ date: currentDays[i].date, time: currentDays[i].time || '' });
                            } else {
                              daysToShow.push({ date: '', time: '' });
                            }
                          }
                          return daysToShow.map((scheduledDay, index) => {
                            const dateError = planValidationErrors[`diasAgendados_${index}_date`];
                            const timeError = planValidationErrors[`diasAgendados_${index}_time`];
                            return (
                              <div key={index} className="flex items-center space-x-1.5 sm:space-x-2">
                                <input
                                  type="date"
                                  data-field={`diasAgendados_${index}_date`}
                                  value={scheduledDay.date}
                                  onChange={(e) => {
                                    const newDays = [...(formData.diasAgendados || [])];
                                    const newValue = e.target.value;
                                    
                                    // Garantir que todos os campos existam
                                    for (let i = 0; i < newDays.length; i++) {
                                      if (!newDays[i]) {
                                        newDays[i] = { date: '', time: '' };
                                      }
                                    }
                                    
                                    // Atualizar apenas o campo específico
                                    const existingTime = newDays[index]?.time || '';
                                    newDays[index] = { date: newValue, time: existingTime };
                                    
                                    setFormData(prev => ({ ...prev, diasAgendados: newDays }));
                                    
                                    // Limpar erro deste campo específico quando preenchido
                                    if (newValue && planValidationErrors[`diasAgendados_${index}_date`]) {
                                      setPlanValidationErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors[`diasAgendados_${index}_date`];
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  className={`flex-1 px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark text-xs sm:text-sm ${
                                    dateError 
                                      ? 'border-red-500 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-900' 
                                      : 'border-border-light dark:border-border-dark'
                                  }`}
                                  required
                                />
                                <input
                                  type="time"
                                  data-field={`diasAgendados_${index}_time`}
                                  value={scheduledDay.time}
                                  onChange={(e) => {
                                    const newDays = [...(formData.diasAgendados || [])];
                                    const newValue = e.target.value;
                                    
                                    // Garantir que todos os campos existam
                                    for (let i = 0; i < newDays.length; i++) {
                                      if (!newDays[i]) {
                                        newDays[i] = { date: '', time: '' };
                                      }
                                    }
                                    
                                    // Se for o primeiro campo, copiar para os demais
                                    if (index === 0) {
                                      for (let i = 0; i < newDays.length; i++) {
                                        newDays[i] = { ...newDays[i], time: newValue };
                                      }
                                    } else {
                                      newDays[index] = { ...newDays[index], time: newValue };
                                    }
                                    
                                    setFormData(prev => ({ ...prev, diasAgendados: newDays }));
                                    
                                    // Limpar erro deste campo específico quando preenchido
                                    if (newValue && planValidationErrors[`diasAgendados_${index}_time`]) {
                                      setPlanValidationErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors[`diasAgendados_${index}_time`];
                                        // Limpar também dos outros campos se foi copiado do primeiro
                                        if (index === 0) {
                                          for (let i = 0; i < newDays.length; i++) {
                                            delete newErrors[`diasAgendados_${i}_time`];
                                          }
                                        }
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  className={`px-2 sm:px-3 py-1.5 sm:py-2 border rounded-lg bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark text-xs sm:text-sm ${
                                    timeError 
                                      ? 'border-red-500 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-900' 
                                      : 'border-border-light dark:border-border-dark'
                                  }`}
                                  required
                                />
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <div className="space-y-1.5 sm:space-y-2">
                        {(() => {
                          const daysToShow = (pet?.diasAgendados || formData.diasAgendados || []).filter(day => day.date && day.time);
                          return daysToShow.length > 0 ? (
                            daysToShow.map((scheduledDay, index) => {
                              // Parse da data como horário local (YYYY-MM-DD)
                              let formattedDate = scheduledDay.date;
                              if (scheduledDay.date) {
                                const [year, month, day] = scheduledDay.date.split('-').map(Number);
                                if (year && month && day) {
                                  const dateObj = new Date(year, month - 1, day);
                                  formattedDate = dateObj.toLocaleDateString('pt-BR');
                                }
                              }
                              return (
                                <div key={index} className="flex items-center space-x-1.5 sm:space-x-2 text-foreground-light dark:text-foreground-dark">
                                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                  <span className="text-xs sm:text-sm">{formattedDate} às {scheduledDay.time}</span>
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark">Nenhum horário agendado</p>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>
      </div>

      {/* Galeria de Fotos do Pet */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-foreground-light dark:text-foreground-dark flex items-center space-x-1.5 sm:space-x-2">
            <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Fotos do Pet</span>
          </h2>
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            {images.length < 4 ? (
              <>
                <input
                  type="file"
                  id="imageUpload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label
                  htmlFor="imageUpload"
                  className="btn-primary flex items-center space-x-1 sm:space-x-2 cursor-pointer text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2"
                >
                  <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Adicionar Fotos ({images.length}/4)</span>
                </label>
              </>
            ) : (
              <div className="text-xs sm:text-sm text-muted-light dark:text-muted-dark bg-muted-light/10 dark:bg-muted-dark/10 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                Limite de 4 fotos atingido
              </div>
            )}
          </div>
        </div>

        {uploading && (
          <div className="mb-3 sm:mb-4">
            <div className="flex items-center justify-between text-xs sm:text-sm text-muted-light dark:text-muted-dark mb-1.5 sm:mb-2">
              <span>Fazendo upload...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
              <div
                className="bg-primary-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {loadingImages ? (
          <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-muted-light dark:text-muted-dark">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-600 mb-2 sm:mb-3"></div>
            <p className="text-xs sm:text-sm">Carregando fotos...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-muted-light dark:text-muted-dark">
            <ImageIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 opacity-50" />
            <p className="text-base sm:text-lg font-medium">Nenhuma foto adicionada</p>
            <p className="text-xs sm:text-sm">Clique em "Adicionar Fotos" para enviar imagens do pet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 min-w-0">
            {images.map((image, index) => (
              <div key={index} className="relative group aspect-square w-full overflow-hidden rounded-lg border border-border-light dark:border-border-dark">
                <img
                  src={image.url}
                  alt={`Foto do pet ${index + 1}`}
                  className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity duration-200"
                  onClick={() => handleImageClick(image)}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center pointer-events-none">
                  <ZoomIn className="w-4 h-4 sm:w-6 sm:h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                {/* Botão de Galeria - apenas se autorizaImagem for verdadeiro */}
                {pet?.autorizaImagem && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetGalleryImage(image);
                    }}
                    className={`absolute top-1.5 sm:top-2 right-1.5 sm:right-2 p-1.5 sm:p-2 rounded-full transition-all duration-200 pointer-events-auto ${
                      galleryImage === image.url
                        ? 'bg-yellow-500 hover:bg-yellow-600'
                        : 'bg-white/80 hover:bg-white'
                    }`}
                    title={galleryImage === image.url ? 'Remover da Galeria Inicial' : 'Adicionar à Galeria Inicial'}
                  >
                    <Star
                      className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                        galleryImage === image.url
                          ? 'text-white fill-white'
                          : 'text-yellow-500'
                      }`}
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Agendamentos do Pet */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-foreground-light dark:text-foreground-dark mb-3 sm:mb-4">Agendamentos do Pet</h2>
        {appointmentsLoading ? (
          <div className="flex items-center justify-center h-24 sm:h-32">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : sortedAppointments.length === 0 ? (
          <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark">Nenhum agendamento encontrado para este pet.</p>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {sortedAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="bg-background-light dark:bg-background-dark rounded-lg p-3 sm:p-4 border border-border-light dark:border-border-dark hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex flex-wrap items-start gap-3 sm:gap-5 flex-1">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="flex items-center space-x-1 sm:space-x-2 text-primary-600 dark:text-primary-400">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">
                          {(() => {
                            if (!appointment.dataAgendamento) return '—';
                            let dateObj;
                            if (appointment.dataAgendamento?.toDate) {
                              dateObj = appointment.dataAgendamento.toDate();
                            } else if (appointment.dataAgendamento instanceof Date) {
                              dateObj = appointment.dataAgendamento;
                            } else {
                              dateObj = new Date(appointment.dataAgendamento);
                            }
                            if (isNaN(dateObj.getTime())) return '—';
                            return `${dateObj.toLocaleDateString('pt-BR')} às ${dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
                          })()}
                        </span>
                      </div>
                      {appointment.nomePet && (
                        <div className="flex items-center space-x-1">
                          <Dog className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                          <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{appointment.nomePet}</span>
                        </div>
                      )}
                    </div>
                    
                    {((appointment.precoServico || appointment.servicePrice) > 0) && (
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1" title="Preço do serviço">
                          <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-success-600 dark:text-success-400" />
                          <span className="text-xs sm:text-sm font-semibold text-success-600 dark:text-success-400">
                            {(appointment.precoServico || appointment.servicePrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        {((appointment.temTransporte || appointment.hasTransport) && ((appointment.precoTransporte || appointment.transportPrice) > 0)) && (
                          <>
                            <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-foreground-light dark:text-foreground-dark" />
                            <div className="flex items-center" title="Preço do transporte">
                              <Car className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400" />
                              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400 -ml-0.5" />
                              <span className="text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 ml-1">
                                {(appointment.precoTransporte || appointment.transportPrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {(appointment.formaPagamento || appointment.paymentMethod) && (
                      <div className="flex items-center space-x-1">
                        <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                          {(() => {
                            const formaPagamento = appointment.formaPagamento || appointment.paymentMethod;
                            return formaPagamento === 'pix' ? 'PIX' : 
                                   formaPagamento === 'cartao' ? 'Cartão' : 
                                   formaPagamento === 'dinheiro' ? 'Dinheiro' : formaPagamento;
                          })()}
                        </span>
                      </div>
                    )}
                    
                    {(appointment.ownernome || appointment.ownerName) && (
                      <div className="flex items-center space-x-1">
                        <UserIcon className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{getFirstName(appointment.ownernome || appointment.ownerName)}</span>
                      </div>
                    )}
                    
                    {appointment.telefone ? (
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="flex items-center space-x-1">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                          <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                            {appointment.telefone}
                          </span>
                        </div>
                        <span className="text-sm sm:text-base font-semibold text-foreground-light dark:text-foreground-dark">
                          • {getServiceTypeLabel(appointment.tipoServico)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm sm:text-base font-semibold text-foreground-light dark:text-foreground-dark">
                        • {getServiceTypeLabel(appointment.tipoServico)}
                      </span>
                    )}
                    
                    {appointment.observacoes && (
                      <div className="flex items-start space-x-1 w-full">
                        <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark mt-0.5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark leading-relaxed">
                          {appointment.observacoes}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    {(() => {
                      if (!appointment.dataAgendamento) return null;
                      
                      let appointmentDate;
                      if (appointment.dataAgendamento?.toDate) {
                        appointmentDate = appointment.dataAgendamento.toDate();
                      } else if (appointment.dataAgendamento instanceof Date) {
                        appointmentDate = appointment.dataAgendamento;
                      } else {
                        appointmentDate = new Date(appointment.dataAgendamento);
                      }
                      
                      if (isNaN(appointmentDate.getTime())) return null;
                      
                      // Adicionar 1 hora ao horário do agendamento
                      const appointmentDatePlusOneHour = new Date(appointmentDate.getTime() + 60 * 60 * 1000);
                      const now = new Date();
                      const isPast = appointmentDatePlusOneHour < now;
                      const shouldShowAgendado = appointment.status === 'agendado' && !isPast;
                      
                      if (!shouldShowAgendado && appointment.status === 'agendado') {
                        return null;
                      }
                      
                      return (
                        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                          appointment.status === 'agendado' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                            : appointment.status === 'concluido'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                        }`}>
                          {appointment.status === 'agendado' ? 'Agendado' : 
                           appointment.status === 'concluido' ? 'Concluído' : 'Em andamento'}
                        </span>
                      );
                    })()}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAppointment(appointment);
                      }}
                      className="p-1.5 sm:p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                      title="Editar agendamento"
                    >
                      <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAppointment(appointment);
                      }}
                      className="p-1.5 sm:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                      title="Excluir agendamento"
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Visualização de Imagem */}
      <GalleryImageModal
        isOpen={showImageModal}
        onClose={closeImageModal}
        selectedImage={selectedImage}
        images={images}
        onNavigate={navigateImage}
        onDelete={handleImageDeleteWithConfirmation}
        onImageClick={closeImageModal}
        imageAlt="Foto do pet em tamanho expandido"
        showDownload
      />

      {/* Modal de Crop de Imagem */}
      {showCropModal && imageToCrop && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={imageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={3/5} // 3:5 vertical para galeria da home
        />
      )}

      {/* Modal de Edição de Agendamento */}
      {showAppointmentModal && (
        <Portal>
          <div 
            className="modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAppointmentModal(false);
                setEditingAppointment(null);
                setAppointmentForm({
                  tipoServico: '',
                  dataAgendamento: '',
                  horaAgendamento: '',
                  servicePrice: '',
                  hasTransport: false,
                  transportPrice: '',
                  paymentMethod: '',
                  observacoes: ''
                });
              }
            }}
          >
            <div className="bg-card-light dark:bg-card-dark rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-foreground-light dark:text-foreground-dark mb-4 flex items-center space-x-2">
                <Edit3 className="w-5 h-5" />
                <span>Editar Agendamento</span>
              </h2>
              
              <form onSubmit={handleAppointmentSubmit} className="space-y-4">
                <div>
                  <label className="label">Tipo de Serviço</label>
                  <input
                    type="text"
                    value={appointmentForm.tipoServico}
                    onChange={(e) => handleAppointmentInputChange('tipoServico', e.target.value)}
                    className="input-field"
                    placeholder="Ex: Banho, Tosa, Banho + Tosa, etc."
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Preço do Serviço (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={appointmentForm.servicePrice}
                      onChange={(e) => handleAppointmentInputChange('servicePrice', e.target.value)}
                      className="input-field"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer mb-1">
                      <input
                        type="checkbox"
                        checked={appointmentForm.hasTransport}
                        onChange={(e) => {
                          handleAppointmentInputChange('hasTransport', e.target.checked);
                          if (!e.target.checked) {
                            handleAppointmentInputChange('transportPrice', '');
                          }
                        }}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-foreground-light dark:text-foreground-dark">Incluir transporte (R$)</span>
                    </label>
                    {appointmentForm.hasTransport && (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={appointmentForm.transportPrice}
                        onChange={(e) => handleAppointmentInputChange('transportPrice', e.target.value)}
                        className="input-field"
                        placeholder="0.00"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Forma de Pagamento</label>
                  <select
                    value={appointmentForm.paymentMethod}
                    onChange={(e) => handleAppointmentInputChange('paymentMethod', e.target.value)}
                    className="input-field"
                  >
                    <option value="">Selecione a forma de pagamento</option>
                    <option value="pix">PIX</option>
                    <option value="cartao">Cartão</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Data</label>
                    <input
                      type="date"
                      value={appointmentForm.dataAgendamento}
                      onChange={(e) => handleAppointmentInputChange('dataAgendamento', e.target.value)}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Hora</label>
                    <input
                      type="time"
                      value={appointmentForm.horaAgendamento}
                      onChange={(e) => handleAppointmentInputChange('horaAgendamento', e.target.value)}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Observações</label>
                  <textarea
                    value={appointmentForm.observacoes}
                    onChange={(e) => handleAppointmentInputChange('observacoes', e.target.value)}
                    className="input-field"
                    placeholder="Observações adicionais..."
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAppointmentModal(false);
                      setEditingAppointment(null);
                      setAppointmentForm({
                        tipoServico: '',
                        dataAgendamento: '',
                        horaAgendamento: '',
                        servicePrice: '',
                        hasTransport: false,
                        transportPrice: '',
                        paymentMethod: '',
                        observacoes: ''
                      });
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submittingAppointment}
                    className="flex-1 btn-primary"
                  >
                    {submittingAppointment ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />
    </div>
  );
};

export default PetDetails;


